import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import LinearProgress from '@mui/material/LinearProgress';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { PageHeader } from '@platform/ui-kit';
import { api } from '../../../api';
import './PlatformAnalysis.css';

/**
 * Strategic Intelligence → Platform Analysis.
 *
 * Ported from the source's `views/discovery/analyze-platform.ejs` and
 * `views/discovery/analyze-progress.ejs` (`/discovery/new`).
 *
 * Point it at a vendor platform and the AI builds a practical profile — how it runs,
 * costs, deploys and connects — scores it against the AI PLATFORM SELECTION CRITERIA, and
 * files it in the AI Platforms catalog.
 *
 * ── SELECTION CRITERIA, NOT THE OPPORTUNITY FRAMEWORK (owner-directed, 2026-08-17) ─────
 *
 * The source scored a platform against the 40-question opportunity assessment framework.
 * That is the wrong instrument and it has been changed: those questions exist to score an
 * OPPORTUNITY's readiness, while `platform_criteria` — Total Cost of Ownership, Security &
 * Compliance, Vendor Lock-in and the rest, each weighted — exists precisely to judge a
 * vendor platform. Nothing on this path reads the framework.
 *
 * COPY IS THE SOURCE'S, sentence for sentence: the heading, the standfirst, both field
 * labels and their hints, the waiting text, the 3-second poll, the 180-second ceiling and
 * every message it can show. The chrome is the platform's MUI, following the convention
 * the sibling Strategic Intelligence screens already set — the source is server-rendered
 * Materialize, so there is no stylesheet to clone.
 *
 * ONE SCREEN, TWO STATES. The source navigates from a form page to a progress page; here
 * they are the same route, because there is no second URL to navigate to and a progress
 * page you cannot reload is not worth one.
 */
export function PlatformAnalysis() {
  const navigate = useNavigate();
  const [name, setName] = useState('');
  const [context, setContext] = useState('');
  const [criteriaCount, setCriteriaCount] = useState<number | null>(null);

  const [jobId, setJobId] = useState<string | null>(null);
  const [running, setRunning] = useState(false);
  const [elapsed, setElapsed] = useState(0);
  const [error, setError] = useState<string | null>(null);

  /** Cleared on unmount so a poll cannot fire into a screen that has gone. */
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(
    () => () => {
      if (timer.current) clearTimeout(timer.current);
    },
    [],
  );

  useEffect(() => {
    api.aiReadiness
      .getPlatformAnalysisCriteria()
      .then((d) => setCriteriaCount(d.criteriaCount))
      .catch(() => setCriteriaCount(null));
  }, []);

  /* The source's loop exactly: first poll after 1.5s, every 3s after that, give up at 180. */
  const poll = useCallback(
    (id: string, seconds: number) => {
      api.aiReadiness
        .getPlatformAnalysisStatus(id)
        .then((d) => {
          if (d.status === 'done' && d.platformId) {
            navigate(
              `/ai_readiness/strategic-intelligence/ai-platforms-catalog/${d.platformId}`,
            );
            return;
          }
          if (d.status === 'error') {
            setRunning(false);
            setError(`Analysis failed: ${d.error || 'unknown error'}.`);
            return;
          }
          const next = seconds + 3;
          if (next > 180) {
            setRunning(false);
            setError(
              'This is taking longer than expected. The analysis may still be running — check the catalog shortly.',
            );
            return;
          }
          setElapsed(next);
          timer.current = setTimeout(() => poll(id, next), 3000);
        })
        .catch(() => {
          const next = seconds + 3;
          if (next > 180) {
            setRunning(false);
            setError('Lost connection while analyzing.');
            return;
          }
          setElapsed(next);
          timer.current = setTimeout(() => poll(id, next), 3000);
        });
    },
    [navigate],
  );

  const analyze = () => {
    const trimmed = name.trim();
    if (!trimmed) {
      setError('Platform name is required.');
      return;
    }
    setError(null);
    setRunning(true);
    setElapsed(0);
    api.aiReadiness
      .startPlatformAnalysis(trimmed, context)
      .then((d) => {
        setJobId(d.jobId);
        timer.current = setTimeout(() => poll(d.jobId, 0), 1500);
      })
      .catch((e: Error) => {
        setRunning(false);
        setError(e.message);
      });
  };

  if (running || jobId) {
    return (
      <Box sx={{ maxWidth: 720 }}>
        <PageHeader title={`Analyzing ${name}`} />
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2.5 }}>
          Building the full profile — capability read, practical heatmap, and Function &amp;
          Industry coverage. This usually takes under a minute. You can keep this tab open; it
          will open the platform automatically when ready.
        </Typography>
        <Card variant="outlined">
          <CardContent>
            {running ? (
              <>
                <LinearProgress sx={{ mb: 2.25, mt: 0.5 }} />
                <Typography sx={{ fontWeight: 500, color: '#1565c0' }}>
                  Working…{elapsed ? ` (${elapsed}s)` : ''}
                </Typography>
              </>
            ) : null}
            {error ? (
              <>
                <Alert severity="error" sx={{ mt: running ? 1.75 : 0 }}>
                  {error}
                </Alert>
                <Stack direction="row" spacing={1} sx={{ mt: 2 }}>
                  <Button
                    variant="contained"
                    onClick={() => {
                      setJobId(null);
                      setError(null);
                      setElapsed(0);
                    }}
                  >
                    Try again
                  </Button>
                  <Button
                    onClick={() =>
                      navigate('/ai_readiness/strategic-intelligence/ai-platforms-catalog')
                    }
                  >
                    Back to catalog
                  </Button>
                </Stack>
              </>
            ) : null}
          </CardContent>
        </Card>
      </Box>
    );
  }

  return (
    <Box sx={{ maxWidth: 820 }}>
      <PageHeader title="Analyze an AI Platform" />
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Enter an AI platform and we&apos;ll analyze it and add it to your AI Platforms catalog
        with a practical profile (how it runs, costs, deploys, and connects) you can review and
        refine.
      </Typography>

      {/*
        THE SELECTION CRITERIA ARE THE ONLY THING A PLATFORM IS SCORED AGAINST.

        Owner-directed, 2026-08-17. This previously read the opportunity assessment
        framework and told you to "add framework questions" — the wrong instrument: those
        questions exist to score an OPPORTUNITY's readiness, not to judge a vendor platform.
      */}
      {criteriaCount === 0 ? (
        <Alert severity="warning" sx={{ mb: 2 }}>
          No AI Platform Selection Criteria are selected, so there is nothing to score a
          platform against. Choose at least one on Settings → AI Platform Selection → Platform
          Fitment Criteria.
        </Alert>
      ) : criteriaCount ? (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
          The platform will be scored against {criteriaCount} selection criteri
          {criteriaCount === 1 ? 'on' : 'a'}.
        </Typography>
      ) : null}

      {error ? (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      ) : null}

      <Card variant="outlined">
        <CardContent>
          <Typography sx={{ fontSize: '1.2rem', mb: 2.5 }}>Platform Details</Typography>
          <TextField
            fullWidth
            required
            label="AI Platform Name"
            helperText="e.g. Anthropic Claude, Dataiku, Cohere"
            value={name}
            onChange={(e) => setName(e.target.value)}
            sx={{ mb: 2.5 }}
          />
          <TextField
            fullWidth
            multiline
            minRows={3}
            label="Context / notes for the analysis (optional)"
            helperText="Anything specific about the client's environment, the opportunity, or what to weigh."
            value={context}
            onChange={(e) => setContext(e.target.value)}
            inputProps={{ maxLength: 2000 }}
          />
        </CardContent>
      </Card>

      {/* The source's own button, not MUI's: `btn-large blue darken-2` with a leading
          `auto_awesome` glyph. See PlatformAnalysis.css for where each value came from. */}
      <Box sx={{ mt: 2.5 }}>
        <button
          type="button"
          className="pa-btn-large"
          onClick={analyze}
          disabled={!name.trim()}
        >
          <i className="material-icons">auto_awesome</i>Analyze
        </button>
        <button
          type="button"
          className="pa-btn-flat"
          onClick={() => navigate('/ai_readiness/strategic-intelligence/ai-platforms-catalog')}
        >
          Cancel
        </button>
      </Box>
    </Box>
  );
}
